import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { generateReceiptHeader, generateReceiptFooter, getCollegeInfo } from './receiptHeaderUtils';
import { ensureRupeeFont, formatINR } from './jspdfFonts';
import { formatDate } from '../utils/dateTimeUtils';
applyPlugin(jsPDF);

export const generateOnlineAdmissionReceipt = async (applicationData, institutionSettings = {}, collegeInfo = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Register Unicode-capable font for ₹ rendering
  const resolvedFont = await ensureRupeeFont(doc);
  const CURRENCY = resolvedFont === 'FreeSans' ? '\u20B9' : 'Rs.';
  
  // Get college info - prefer parameter, then institutionSettings, then localStorage
  if (!collegeInfo) {
    collegeInfo = institutionSettings;
    if (!collegeInfo || Object.keys(collegeInfo).length === 0) {
      collegeInfo = getCollegeInfo() || {};
      console.warn('generateOnlineAdmissionReceipt: collegeInfo not passed, using localStorage fallback. This may cause tenant isolation issues.');
    }
  }
  
  // Determine title based on payment status
  const isPaid = applicationData.payment_status === 'Success' && applicationData.payment_required !== false;
  const receiptTitle = isPaid ? 'ONLINE ADMISSION RECEIPT' : 'APPLICATION ACKNOWLEDGEMENT';
  
  // Generate common header with tenant branding
  let yPos = await generateReceiptHeader(doc, collegeInfo, receiptTitle);

  // Receipt Details
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'normal');
  doc.setTextColor(0, 0, 0);
  
  const leftCol = 20;
  const rightCol = 120;

  doc.setFont(resolvedFont, 'bold');
  doc.text('Receipt No:', leftCol, yPos);
  doc.setFont(resolvedFont, 'normal');
  doc.text(applicationData.receipt_number || 'N/A', leftCol + 30, yPos);

  doc.setFont(resolvedFont, 'bold');
  doc.text('Application No:', rightCol, yPos);
  doc.setFont(resolvedFont, 'normal');
  doc.text(applicationData.application_number || 'N/A', rightCol + 35, yPos);

  yPos += 7;
  doc.setFont(resolvedFont, 'bold');
  doc.text('Date:', leftCol, yPos);
  doc.setFont(resolvedFont, 'normal');
  doc.text(formatDate(applicationData.payment_date || new Date()), leftCol + 30, yPos);

  // Check if payment was actually made
  const paymentMade = applicationData.payment_status === 'Success' && applicationData.payment_required !== false;
  const paymentNotRequired = applicationData.payment_required === false;

  doc.setFont(resolvedFont, 'bold');
  doc.text('Payment Status:', rightCol, yPos);
  doc.setFont(resolvedFont, 'normal');
  
  if (paymentMade) {
    doc.setTextColor(0, 128, 0); // Green
    doc.text('PAID', rightCol + 35, yPos);
  } else if (paymentNotRequired) {
    doc.setTextColor(255, 140, 0); // Orange
    doc.text('PENDING (Pay at Institute)', rightCol + 35, yPos);
  } else {
    doc.setTextColor(255, 0, 0); // Red
    doc.text('PENDING', rightCol + 35, yPos);
  }
  doc.setTextColor(0, 0, 0);

  // Student Information
  yPos += 15;
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Student Information', leftCol, yPos);
  
  yPos += 8;
  doc.setFontSize(10);
  
  const studentInfo = [
    ['Name:', applicationData.full_name || 'N/A'],
    ['Email:', applicationData.email || 'N/A'],
    ['Phone:', applicationData.phone || 'N/A'],
    ['Date of Birth:', applicationData.date_of_birth ? formatDate(applicationData.date_of_birth) : 'N/A'],
    ['Course:', applicationData.course_name || 'N/A'],
    ['Session:', applicationData.session || 'N/A'],
    ['Entry Type:', applicationData.is_lateral_entry ? 'Lateral Entry' : 'Regular'],
  ];

  studentInfo.forEach(([label, value]) => {
    doc.setFont(resolvedFont, 'bold');
    doc.text(label, leftCol, yPos);
    doc.setFont(resolvedFont, 'normal');
    doc.text(value, leftCol + 35, yPos);
    yPos += 6;
  });

  // Fee Details Section (renamed from Payment Details)
  yPos += 5;
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text(paymentMade ? 'Payment Details' : 'Fee Details', leftCol, yPos);
  
  yPos += 8;
  doc.setFontSize(10);

  // Determine booking fee and admission fee
  const bookingFee = applicationData.booking_fee || applicationData.payment_amount || 0;
  const admissionFee = applicationData.admission_fee || 0;
  
  // Actual paid amount is booking fee (when payment made)
  const actualPaidAmount = paymentMade ? bookingFee : 0;
  const balanceDue = paymentNotRequired ? bookingFee : (admissionFee - actualPaidAmount);

  // Payment/Fee table - different based on payment status
  let tableBody = [];
  let tableFoot = [];
  
  if (paymentMade) {
    // Payment was made - show booking fee paid
    tableBody = [
      ['Seat Booking Fee', `${CURRENCY} ${formatINR(bookingFee)}`],
    ];
    tableFoot = [['Total Paid', `${CURRENCY} ${formatINR(actualPaidAmount)}`]];
    
    // If there's remaining admission fee to pay
    if (admissionFee > bookingFee) {
      tableBody.push(['Balance Admission Fee (Pay at Institute)', `${CURRENCY} ${formatINR(admissionFee - bookingFee)}`]);
    }
  } else {
    // Payment not made - show only booking fee that's due
    tableBody = [
      ['Seat Booking Fee', `${CURRENCY} ${formatINR(bookingFee)}`],
      ['Amount Paid', `${CURRENCY} 0`],
    ];
    tableFoot = [['Balance Due (Booking Fee)', `${CURRENCY} ${formatINR(bookingFee)}`]];
  }

  // Payment/Fee table
  doc.autoTable({
    startY: yPos,
    head: [['Description', 'Amount']],
    body: tableBody,
    foot: tableFoot,
    theme: 'striped',
    headStyles: { fillColor: [0, 102, 204], textColor: 255 },
    footStyles: { 
      fillColor: paymentMade ? [200, 255, 200] : [255, 230, 200], 
      textColor: 0, 
      fontStyle: 'bold' 
    },
    styles: { fontSize: 10, font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 120 },
      1: { cellWidth: 70, halign: 'right', fontStyle: 'bold' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Payment Gateway Info (only if payment was made)
  if (applicationData.razorpay_payment_id && paymentMade) {
    doc.setFontSize(9);
    doc.setFont(resolvedFont, 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Payment ID: ${applicationData.razorpay_payment_id}`, leftCol, yPos);
    yPos += 5;
    doc.text(`Order ID: ${applicationData.razorpay_order_id || 'N/A'}`, leftCol, yPos);
    yPos += 5;
    doc.text('Payment Gateway: Razorpay', leftCol, yPos);
    yPos += 10;
    doc.setTextColor(0, 0, 0);
  }

  // Next Steps - Different based on payment status
  yPos += 5;
  doc.setFontSize(11);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Next Steps:', leftCol, yPos);
  
  yPos += 7;
  doc.setFontSize(9);
  doc.setFont(resolvedFont, 'normal');
  
  let steps;
  if (paymentMade) {
    steps = [
      '1. Your application is under review by our admission team',
      '2. You will receive an email notification once your application is approved',
      '3. After approval, you can collect your admission letter from the institute',
      '4. Keep this receipt for future reference'
    ];
  } else {
    steps = [
      '1. Your application has been submitted successfully',
      '2. Please visit the institute to complete the admission fee payment',
      '3. Bring this acknowledgement slip and required documents',
      '4. After payment, you will receive the official admission receipt',
      '5. For payment queries, please contact the admission office'
    ];
  }
  
  steps.forEach(step => {
    doc.text(step, leftCol + 5, yPos);
    yPos += 5;
  });

  // Important Notice for unpaid applications
  if (!paymentMade) {
    yPos += 5;
    doc.setFontSize(10);
    doc.setFont(resolvedFont, 'bold');
    doc.setTextColor(255, 0, 0);
    doc.text('IMPORTANT: This is NOT a payment receipt. Fee payment is pending.', leftCol, yPos);
    doc.setTextColor(0, 0, 0);
    yPos += 7;
    doc.setFont(resolvedFont, 'normal');
    doc.setFontSize(9);
    doc.text(`Booking Fee Due: ${CURRENCY} ${formatINR(bookingFee)}`, leftCol, yPos);
  }

  // Note about remaining admission fee (when booking fee is paid)
  if (paymentMade && admissionFee > bookingFee) {
    yPos += 5;
    doc.setFontSize(9);
    doc.setFont(resolvedFont, 'bold');
    doc.setTextColor(0, 100, 0);
    doc.text(`Note: Balance Admission Fee of ${CURRENCY} ${formatINR(admissionFee - bookingFee)} is payable at the institute.`, leftCol, yPos);
    doc.setTextColor(0, 0, 0);
  }

  // Footer
  yPos = pageHeight - 40;
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'italic');
  doc.text(paymentMade ? 'This is a computer-generated receipt' : 'This is a computer-generated acknowledgement', pageWidth / 2, yPos, { align: 'center' });
  yPos += 5;
  doc.text('No signature required', pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 10;
  doc.setFontSize(8);
  doc.setFont(resolvedFont, 'normal');
  doc.text('For queries, please contact the admission office', pageWidth / 2, yPos, { align: 'center' });

  // Save PDF with appropriate name
  const fileName = paymentMade 
    ? `Booking_Receipt_${applicationData.receipt_number || 'Receipt'}.pdf`
    : `Application_Acknowledgement_${applicationData.application_number || 'App'}.pdf`;
  doc.save(fileName);

  return doc;
};
